import { useCallback, useEffect } from 'react';
import { useFetch } from 'use-http';
import { useNavigate } from 'react-router-dom';
import jwt from 'jwt-decode';

import config from '../../../config';
import { useLocalStorage } from 'usehooks-ts';
import { UserRole } from '../users/user.role';

interface LoginData {
  email: string;
  password: string;
}

interface LoginHook {
  login: (data: LoginData) => void;
  loading: boolean;
  error: Error | undefined;
}

const { api } = config;
const { baseUrl, loginPath } = api;

export default function useLogin(): LoginHook {
  const { post, loading, error, response } = useFetch(`${baseUrl}${loginPath}`);
  const [token, setToken] = useLocalStorage('token', { bearer: '' });
  const [role, setRole] = useLocalStorage('role', { role: '' });
  const [, setIsOnboarded] = useLocalStorage('is_onboarded', false);
  const navigate = useNavigate();

  useEffect(() => {
    if (response.ok && token.bearer !== '' && role.role !== '') {
      navigate(role.role === UserRole.ADMIN ? config.routes.dashboard : config.routes.invoices);
    }
  }, [navigate, response, role.role, token]);

  const login = useCallback(
    async (data: LoginData) => {
      try {
        const r = await post(data);
        setToken({ bearer: r.access_token });
        const decoded: Record<string, string> = jwt(r.access_token);
        setRole({ role: decoded.role });
        setIsOnboarded(r.is_onboarded);
      } catch {
        console.log('Invalid credentials -> useLogin');
      }
    },
    [post, setToken, setRole, setIsOnboarded],
  );

  return {
    login,
    loading,
    error,
  };
}
